import { useCallback, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Container, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

import TeamInvitesSection from './TeamInvitesSection';
import TeamMembersTable from './TeamMembersTable';
import { getInviteErrorMessage } from './utils/invites';
import useApp from '../../hooks/useApp';
import useTeam from '../../hooks/useTeam';
import { getErrorMessage, isNotAuthorizedError } from '../../lib/error';
import { getEmail } from '../../lib/jwt';
import Routes from '../../lib/routes';
import { getSDKClient, TeamInvite, TeamMember } from '../../lib/sdk';
import { pluralize } from '../../utils/string';

export default function TeamMembersV2(): JSX.Element {
  const { app } = useApp();
  const { team } = useTeam();
  const { enqueueSnackbar } = useSnackbar();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invites, setInvites] = useState<TeamInvite[]>([]);
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);

  const token = app.tokens.get(app.env) ?? '';
  const getSDK = useCallback(() => getSDKClient(app.env, token), [app.env, token]);

  const getTeamMembers = useCallback(async () => {
    try {
      const resp = await getSDK().getTeamMembers(team.team_id);
      setMembers(resp.team_members);
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  }, [getSDK, team, enqueueSnackbar]);

  const getTeamInvites = useCallback(async () => {
    try {
      const resp = await getSDK().getTeamInvites(team.team_id);
      setInvites(resp.team_invites);
    } catch (err) {
      // only teamOwner and teamManager can see invites
      if (isNotAuthorizedError(err)) {
        setInvites([]);
        return;
      }
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  }, [getSDK, team, enqueueSnackbar]);

  // runs an action, then shows the outcome and refreshes; returns whether the action succeeded
  const runAction = async (
    action: () => Promise<void>,
    message: string,
    refresh: () => Promise<void>,
    toErrorMessage = getErrorMessage,
  ) => {
    setIsRequestPending(true);
    let ok = false;
    try {
      await action();
      enqueueSnackbar(message, { variant: 'success' });
      ok = true;
    } catch (err) {
      enqueueSnackbar(toErrorMessage(err), { variant: 'error' });
    }
    await refresh();
    setIsRequestPending(false);
    return ok;
  };

  const updateMemberRoles = (member: TeamMember, roles: string[]) =>
    runAction(
      () => getSDK().updateTeamMemberRoles(team.team_id, member.developer_id, roles),
      `Roles updated for ${member.email}`,
      getTeamMembers,
    );

  const removeMember = (member: TeamMember) =>
    runAction(
      () => getSDK().removeTeamMember(team.team_id, member.developer_id),
      `${member.email} was removed from the team`,
      getTeamMembers,
    );

  const resendInvite = (invite: TeamInvite) =>
    runAction(
      () => getSDK().resendTeamInvite(team.team_id, invite.invite_id),
      `Invite resent to ${invite.invitee_email}`,
      getTeamInvites,
      getInviteErrorMessage,
    );

  const cancelInvite = (invite: TeamInvite) =>
    runAction(
      () => getSDK().cancelTeamInvite(team.team_id, invite.invite_id),
      `Invite to ${invite.invitee_email} was cancelled`,
      getTeamInvites,
      getInviteErrorMessage,
    );

  useEffect(() => {
    if (team.team_id === '') {
      return;
    }
    getTeamMembers();
    getTeamInvites();
  }, [team, getTeamMembers, getTeamInvites]);

  return (
    <Container maxWidth="lg" sx={{ flexGrow: 1, p: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', justifyContent: 'space-between' }}
        >
          <Box>
            <Typography variant="h1" component="h1" gutterBottom>
              Members
            </Typography>
            <Typography color="text.secondary">
              <strong>{team.team_name}</strong> · {pluralize(members.length, 'member')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            component={Link}
            to={Routes.Teams.InviteMemberV2}
            draggable={false}
            startIcon={<AddIcon />}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Invite member
          </Button>
        </Box>
        <TeamMembersTable
          members={members}
          callerEmail={getEmail(token)}
          isRequestPending={isRequestPending}
          updateMemberRolesAction={updateMemberRoles}
          removeMemberAction={removeMember}
        />
        <TeamInvitesSection
          invites={invites}
          isRequestPending={isRequestPending}
          resendInviteAction={resendInvite}
          cancelInviteAction={cancelInvite}
        />
      </Box>
    </Container>
  );
}
